<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-6 overflow-auto">
    <div class="w-full flex justify-end shrink-0">
      <input
        v-model="searchQuery"
        type="text"
        class="w-full min-w-0 px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white md:max-w-[220px]"
        placeholder="Search by name..."
      >
    </div>

    <div class="w-full overflow-x-auto overflow-y-auto max-h-96">
      <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm equal-cols">
        <thead>
          <tr>
            <th class="text-center">No.</th>
            <th class="text-center">Image</th>
            <th>Name</th>
            <th>Created At</th>
            <th class="text-center">Is central</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(dep, index) in filteredDepartments"
            :key="dep.id"
            class="hover:bg-slate-100/50 transition-colors group"
          >
            <td class="text-center font-medium text-slate-400">{{ index + 1 }}</td>
            <td class="text-center">
              <div class="inline-flex">
                <img
                  v-if="dep.image"
                  :src="resolveCompanyLogoUrl(dep.image)"
                  class="w-7 h-6 object-contain"
                >
                <div
                  v-else
                  class="w-7 h-6 rounded-md bg-slate-100 flex items-center justify-center text-[10px] text-slate-400 font-bold uppercase shrink-0"
                >
                  {{ dep.name?.charAt(0) || '?' }}
                </div>
              </div>
            </td>
            <td class="font-semibold text-slate-700">{{ dep.name }}</td>
            <td>{{ formatDate(dep.created_at) }}</td>
            <td class="text-center">
              <input
                type="checkbox"
                class="w-4 h-4 accent-[#0d4169]"
                :class="[
                  dep.__togglingIsCentral ? 'opacity-50 pointer-events-none' : '',
                  canEditIsCentral ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                ]"
                :checked="!!(dep.isCentral ?? dep.is_central)"
                :disabled="!canEditIsCentral || !!dep.__togglingIsCentral"
                @change="toggleIsCentral(dep, $event)"
              >
            </td>
          </tr>
          <tr v-if="loading">
            <td colspan="5" class="text-center py-10">
              <div class="flex items-center justify-center">
                <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </td>
          </tr>
          <tr v-else-if="error">
            <td colspan="5" class="text-center py-10 text-red-500">{{ error }}</td>
          </tr>
          <tr v-else-if="filteredDepartments.length === 0">
            <td colspan="5" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
              No active departments found for this company.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import store from '../../store'
import { apiFetch, resolveCompanyLogoUrl } from '../../utils/api'

const departments = ref([])
const loading = ref(false)
const error = ref('')
const searchQuery = ref('')

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
const cntKitchenDeptPrivilege = computed(() =>
  userPrivileges.value?.['Cnt. Kitchen Departments'] ||
  userPrivileges.value?.['cnt. kitchen departments'] ||
  userPrivileges.value?.['Cnt. Kitchen'] ||
  userPrivileges.value?.['cnt. kitchen'] ||
  null
)
const canEditIsCentral = computed(() => hasAdminPrivilege.value || !!cntKitchenDeptPrivilege.value?.Edit)

const filteredDepartments = computed(() => {
  let result = [...departments.value]
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((dep) =>
      String(dep.name || '').toLowerCase().includes(query)
    )
  }
  return result
})

function formatDate(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toISOString().slice(0, 10)
}

async function loadDepartments() {
  loading.value = true
  error.value = ''
  if (!companyId.value) {
    departments.value = []
    error.value = 'Company ID not found in user profile.'
    loading.value = false
    return
  }

  try {
    let endpoint = `/departments/company/${companyId.value}`
    if (branchId.value) {
      endpoint = `/departments/branch/${branchId.value}`
    }

    const response = await apiFetch(endpoint, { method: 'GET' })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to load departments')
    }
    const data = await response.json()
    const list = Array.isArray(data) ? data : []
    // Active departments only (status === 1)
    departments.value = list
      .filter((dep) => (dep.status ?? 1) === 1)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  } catch (err) {
    error.value = err.message || 'Failed to load departments'
    departments.value = []
  } finally {
    loading.value = false
  }
}

async function toggleIsCentral(dep, event) {
  if (!canEditIsCentral.value) {
    if (event?.target) {
      event.target.checked = !!(dep.isCentral ?? dep.is_central)
    }
    return
  }
  if (!dep?.id || dep.__togglingIsCentral) return

  const next = !!(event?.target?.checked)
  dep.__togglingIsCentral = true
  try {
    const res = await apiFetch(`/departments/${dep.id}/is-central`, {
      method: 'PATCH',
      body: JSON.stringify({ isCentral: next }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to update Is central')
    }
    const updated = await res.json().catch(() => null)
    dep.isCentral = updated?.isCentral ?? next
    if ('is_central' in dep) dep.is_central = dep.isCentral
  } catch (e) {
    if (event?.target) {
      event.target.checked = !!(dep.isCentral ?? dep.is_central)
    }
    alert(e.message || 'Failed to update Is central')
  } finally {
    dep.__togglingIsCentral = false
  }
}

watch(companyId, () => {
  loadDepartments()
})

watch(branchId, () => {
  loadDepartments()
})

onMounted(() => {
  loadDepartments()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  color: #1e293b;
}

table.equal-cols {
  table-layout: fixed;
}

table.equal-cols th,
table.equal-cols td {
  width: 20%;
}

th,
td {
  padding: 5px 6px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.8rem;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}
</style>
